import React, { useEffect, useState } from 'react';
import { Box, Typography, Popover, IconButton, Button, TextField } from '@mui/material';
import { CloseIcon } from 'icons/materialIcons';
import dayjs from 'dayjs';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const TeamTimePopover = ({
    open,
    anchorEl,
    onClose,
    title = 'Select Time',
    value = '09:00',
    onChange
}) => {
    const [selectedTime, setSelectedTime] = useState(dayjs().hour(9).minute(0).second(0));
    const [typedTime, setTypedTime] = useState('09:00');
    const [meridiem, setMeridiem] = useState('AM');

    const syncFromDayjs = (timeValue) => {
        const hour24 = timeValue.hour();
        const minute = timeValue.minute();
        const isPM = hour24 >= 12;
        const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
        setTypedTime(`${String(hour12).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
        setMeridiem(isPM ? 'PM' : 'AM');
    };

    const buildDayjsFrom12Hour = (timeText, amPm) => {
        const match = timeText.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        const h12 = Number(match[1]);
        const m = Number(match[2]);
        if (Number.isNaN(h12) || Number.isNaN(m) || h12 < 1 || h12 > 12 || m < 0 || m > 59) return null;

        let hour24 = h12 % 12;
        if (amPm === 'PM') hour24 += 12;
        return dayjs().hour(hour24).minute(m).second(0);
    };

    useEffect(() => {
        const [h = '09', m = '00'] = (value || '09:00').split(':');
        const parsed = dayjs().hour(Number(h)).minute(Number(m)).second(0);
        setSelectedTime(parsed);
        syncFromDayjs(parsed);
    }, [value, open]);

    const applyTime = () => {
        const parsed = buildDayjsFrom12Hour(typedTime, meridiem);
        const finalTime = parsed || selectedTime;
        onChange?.(finalTime.format('HH:mm'));
        onClose?.();
    };

    const setQuick = (minutesToAdd) => {
        const now = dayjs().add(minutesToAdd, 'minute').second(0);
        setSelectedTime(now);
        syncFromDayjs(now);
    };

    const handleTypedTimeChange = (event) => {
        const valueInput = event.target.value;
        setTypedTime(valueInput);

        const match = valueInput.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return;

        const parsed = buildDayjsFrom12Hour(valueInput, meridiem);
        if (!parsed) return;
        setSelectedTime(parsed);
    };

    const updateBySteps = (unit, delta) => {
        const base = selectedTime || dayjs().hour(0).minute(0).second(0);
        const updated = unit === 'hour'
            ? base.add(delta, 'hour')
            : base.add(delta, 'minute');
        const normalized = dayjs()
            .hour(updated.hour())
            .minute(updated.minute())
            .second(0);
        setSelectedTime(normalized);
        syncFromDayjs(normalized);
    };

    const handleMeridiemChange = (nextMeridiem) => {
        setMeridiem(nextMeridiem);
        const parsed = buildDayjsFrom12Hour(typedTime, nextMeridiem);
        if (parsed) {
            setSelectedTime(parsed);
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ zIndex: 1300, mt: 0.5 }}
            PaperProps={{
                sx: {
                    width: 360,
                    borderRadius: '16px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    padding: 0,
                    overflow: 'hidden'
                }
            }}
        >
            <Box
                sx={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                    {title}
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <TextField
                        label="Time (hh:mm)"
                        value={typedTime}
                        onChange={handleTypedTimeChange}
                        placeholder="01:26"
                        size="small"
                        sx={{
                            width: 118,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#f7f7f7',
                                borderRadius: '12px',
                                '& fieldset': { border: 'none' }
                            }
                        }}
                    />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#555', mt: 1, ml: 0.25 }}>
                        {meridiem}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                        variant={meridiem === 'AM' ? 'contained' : 'outlined'}
                        onClick={() => handleMeridiemChange('AM')}
                        sx={{
                            borderRadius: '20px',
                            textTransform: 'none',
                            minWidth: 72,
                            ...(meridiem === 'AM'
                                ? { backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#333' } }
                                : { borderColor: '#e0e0e0', color: '#666' })
                        }}
                    >
                        AM
                    </Button>
                    <Button
                        variant={meridiem === 'PM' ? 'contained' : 'outlined'}
                        onClick={() => handleMeridiemChange('PM')}
                        sx={{
                            borderRadius: '20px',
                            textTransform: 'none',
                            minWidth: 72,
                            ...(meridiem === 'PM'
                                ? { backgroundColor: '#000', color: '#fff', '&:hover': { backgroundColor: '#333' } }
                                : { borderColor: '#e0e0e0', color: '#666' })
                        }}
                    >
                        PM
                    </Button>
                </Box>

                <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#fafafa'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>Hour</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => updateBySteps('hour', 1)} sx={{ p: 0.25 }}>
                                <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 28, textAlign: 'center' }}>
                                {typedTime.split(':')[0]}
                            </Typography>
                            <IconButton size="small" onClick={() => updateBySteps('hour', -1)} sx={{ p: 0.25 }}>
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#555' }}>:</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>Minute</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => updateBySteps('minute', 1)} sx={{ p: 0.25 }}>
                                <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 28, textAlign: 'center' }}>
                                {typedTime.split(':')[1] || '00'}
                            </Typography>
                            <IconButton size="small" onClick={() => updateBySteps('minute', -1)} sx={{ p: 0.25 }}>
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={{ minWidth: 44, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#555' }}>
                            {meridiem}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setQuick(0)}
                        sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#e0e0e0', color: '#666' }}
                    >
                        Now
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setQuick(15)}
                        sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#e0e0e0', color: '#666' }}
                    >
                        +15 min
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setQuick(30)}
                        sx={{ borderRadius: '20px', textTransform: 'none', borderColor: '#e0e0e0', color: '#666' }}
                    >
                        +30 min
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        backgroundColor: 'white',
                        color: '#828282',
                        borderRadius: '20px',
                        border: 'none',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        '&:hover': { backgroundColor: 'white', boxShadow: 2 }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={applyTime}
                    sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        '&:hover': { backgroundColor: '#333' }
                    }}
                >
                    Apply
                </Button>
            </Box>
        </Popover>
    );
};

export default TeamTimePopover;
